import React, { useState, useEffect } from "react";
import Container from "./ui/Container";
import Button from "./ui/Button";
import Logo from "./ui/Logo";
import { cn } from "@/lib/utils";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { title: "Home", href: "/" },
    { title: "Services", href: "#services" },
    { title: "About", href: "#about" },
    { title: "Our Work", href: "#work" },
    { title: "Contact", href: "#contact" },
  ];

  // Handle smooth scrolling
  const handleGetStartedClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      setIsMobileMenuOpen(false); // Close mobile menu if open
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-4",
          isScrolled
            ? "bg-white bg-opacity-80 backdrop-blur-lg shadow-sm py-3"
            : "bg-transparent"
        )}
      >
        <Container className="flex items-center justify-between">
          <div className="flex items-center">
            <a href="/" className="flex items-center">
              <Logo size="md" />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <ul className="flex space-x-8">
              {navLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-gray-800 hover:text-tech-blue transition-colors duration-300 link-underline font-medium"
                  >
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>
            <Button 
              onClick={handleGetStartedClick}
              className="cursor-pointer"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-800 hover:text-tech-blue focus:outline-none"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </Container>
      </nav>

      {/* Mobile Menu */}
      <div
        className={cn(
          "fixed inset-0 z-40 md:hidden transition-all duration-300",
          isMobileMenuOpen ? "visible" : "invisible"
        )}
      >
        {/* Overlay */}
        <div 
          className={cn(
            "fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300",
            isMobileMenuOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div 
          className={cn(
            "fixed right-0 top-0 h-full w-full max-w-sm bg-white shadow-xl transition-transform duration-300",
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          )}
        >
          <div className="h-full overflow-y-auto">
            <div className="pt-24 px-6">
              <ul className="space-y-6">
                {navLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-gray-800 hover:text-tech-blue transition-colors duration-300 text-lg font-medium block"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.title}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-8 mb-8">
                <Button 
                  onClick={handleGetStartedClick}
                  className="w-full justify-center cursor-pointer"
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
